import produce from 'immer';
import { QueueDashboardQueueSortBy } from 'lib-dashboard-types';
import {
  ReadQueuesApiArg,
  ReadQueuesSortKey,
  readQueuesSortKeys,
  Sort,
} from 'lib-shared-types-api';
import { useCallback, useMemo } from 'react';

import { getSortObjectFromString } from 'utils/url/getSortObjectFromString';
import { useAppSearchParams } from 'utils/url/useAppSearchParams';
import { useCurrentUser } from 'utils/user';

/** The default queues page limit. */
const defaultPageLimit = 50;

type UseQueueDashboardParamsToApiArgResult = {
  /** Function to convert current params to ReadQueuesApiArg */
  readonly queueParamsToApiArg: () => ReadQueuesApiArg;
  /** Function to convert sort string to Sort object */
  readonly queueSortStringToObject: (
    sort_by?: QueueDashboardQueueSortBy,
  ) => Sort<ReadQueuesSortKey> | Sort<ReadQueuesSortKey>[] | undefined;
};

/**
 * A hook that handles provides methods for mapping the queue dashboard URL
 * search params to the API argument format for fetching queues.
 */
export const useQueueDashboardParamsToApiArg =
  (): UseQueueDashboardParamsToApiArgResult => {
    const { user } = useCurrentUser();
    const { params } = useAppSearchParams('/dashboard/queues');

    const queueSortStringToObject = useCallback<
      UseQueueDashboardParamsToApiArgResult['queueSortStringToObject']
    >(
      (sort_by) => {
        // Don't apply any default sorting when viewing favorites or custom queues and no explicit
        // sort_by parameter is set, in which case the sorting is determined by the queues order.
        if (!sort_by) {
          if (params.type === 'custom') return undefined;
          if (params.type === 'favourite' && params.id) return undefined;
        }

        const sort = getSortObjectFromString<ReadQueuesSortKey>(
          sort_by ?? 'name:asc',
          readQueuesSortKeys,
        )!;

        // If we're sorting by name, we leave it as is, otherwise we sort by the requested sort key
        // and then by name in ascending order.
        return sort.key === 'name'
          ? sort
          : [sort, { key: 'name', direction: 'asc' }];
      },
      [params.id, params.type],
    );

    const queueParamsToApiArg = useCallback<
      UseQueueDashboardParamsToApiArgResult['queueParamsToApiArg']
    >(
      () =>
        produce<ReadQueuesApiArg>(
          {
            agentId: params.type === 'agent' ? user.id : undefined,
            queueIds: undefined,
            searchTerm: params.q,
            limit: params.limit ? parseInt(params.limit) : defaultPageLimit,
            offset: params.offset ? parseInt(params.offset) : undefined,
            sort: queueSortStringToObject(params.sort_by),
            isArchived:
              params.type === 'custom' || params.type === 'favourite'
                ? undefined
                : params.type === 'archived',
          },
          (draft) => {
            if (params.type === 'custom') {
              draft.queueIds = params.queue ? [params.queue].flat() : undefined;
            }
          },
        ),
      [
        params.limit,
        params.offset,
        params.q,
        params.queue,
        params.sort_by,
        params.type,
        queueSortStringToObject,
        user.id,
      ],
    );

    return useMemo(
      () => ({ queueParamsToApiArg, queueSortStringToObject }),
      [queueParamsToApiArg, queueSortStringToObject],
    );
  };
